import React, { useEffect, useState, useMemo } from "react";
import {
  TableContainer,
  Container,
  Grid,
  Box,
  Paper,
  LinearProgress,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  Button,
  Alert,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import useSWR, { mutate as globalMutate } from "swr";
import ProductionStatusDetails from "./ProductionStatusDetails";
import ProductionEdit from "../expenses/update";
import ExpenseDetailsEdit from "../expense_details/update";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction";
import Drawer from "@mui/material/Drawer";
import TableSortLabel from "@mui/material/TableSortLabel";
import RadioGroup from "@mui/material/RadioGroup";
import {
  getExpectedEpisodesPerRecording,
  getRecordingBudgetMismatch,
  formatRecordingMismatchTooltip,
  hasRecordingEpisodeCountMismatch,
  isPerRecordingBillingType,
} from "../../../components/utils/recordingBudgetUtils";
import Radio from "@mui/material/Radio";

const formatEpisodeRange = (episodes) => {
  if (!Array.isArray(episodes) || episodes.length === 0) return "";

  const sorted = [...episodes].sort((a, b) => a - b);
  const ranges = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === rangeEnd + 1) {
      rangeEnd = sorted[i];
    } else {
      ranges.push({ start: rangeStart, end: rangeEnd });
      rangeStart = sorted[i];
      rangeEnd = sorted[i];
    }
  }
  ranges.push({ start: rangeStart, end: rangeEnd });

  return ranges
    .map(({ start, end }) =>
      start === end ? `E-${start}` : `E-${start} -> E-${end}`
    )
    .join(", ");
};

const getLastEpisodeOfRecording = (recording) => {
  const episodes = Array.isArray(recording?.episodes) ? recording.episodes : [];
  if (episodes.length === 0) return null;
  return Math.max(...episodes);
};

const getEpisodeUnamortizedValue = (episodeNumber, dataRows, maxEpisodes) => {
  if (!episodeNumber) return 0;

  const field = `episode-${episodeNumber}`;
  const totalOfFullAssigned = dataRows.reduce(
    (sum, row) => sum + (parseFloat(row[field]?.maxEpAm) || 0),
    0
  );

  return totalOfFullAssigned * (maxEpisodes - episodeNumber + 1);
};

const hasEpisodeDisplayData = (episodeData) =>
  episodeData &&
  ((episodeData.amount !== null && episodeData.amount !== undefined) ||
    episodeData.exp_pred ||
    episodeData.expPred);

const EpisodeGrid = () => {
  const [threshold, setThreshold] = useState(0.7);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedRecordingEpisodes, setSelectedRecordingEpisodes] = useState(null);
  const [selectedRecordingLabel, setSelectedRecordingLabel] = useState(null);
  const [selectedRecordingEpisodeRange, setSelectedRecordingEpisodeRange] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sortConfig, setSortConfig] = useState({ field: "id", direction: "asc" });
  // New state for display options
  const [displayMode, setDisplayMode] = useState("all"); // "all", "recording", "unlocked", "recordings"
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [recordingMismatchAlertExpanded, setRecordingMismatchAlertExpanded] = useState(false);
  const [editExpenseId, setEditExpenseId] = useState(null);
  const [editExpenseDetailsId, setEditExpenseDetailsId] = useState(null);

  const getRecordingAvgData = (episodeData, recording) => {
    const episodes = Array.isArray(recording.episodes) ? recording.episodes : [];
    if (episodes.length === 0) {
      return { amount: null, exp_pred: false, totalAmount: 0 };
    }

    let total = 0;
    let hasPred = false;
    let hasData = false;

    episodes.forEach((ep) => {
      const epData = episodeData[`episode-${ep}`];
      if (epData?.amount !== null && epData?.amount !== undefined) {
        total += epData.amount;
        hasData = true;
      }
      if (epData?.expPred) {
        hasPred = true;
        hasData = true;
      }
    });

    if (!hasData) {
      return { amount: null, exp_pred: false, totalAmount: 0 };
    }

    return {
      amount: total / episodes.length,
      exp_pred: hasPred,
      totalAmount: total,
    };
  };

  const { data: productions, error: productionsError } = useSWR("/productions");
  const { data: categoryItems, error: categoryError } = useSWR(
    selectedProduction?.id
      ? `/production-category-item/of-production/${selectedProduction.id}`
      : null
  );
  const { data: billingTypes } = useSWR("/production-category-item-type");
  
  // Fetch recordings for the selected production
  const { data: recordings, error: recordingsError } = useSWR(
    selectedProduction?.id
      ? `/recordings/of-production/${selectedProduction.id}`
      : null
  );

  const { data: itemsDetails, error: detailsError, mutate: mutateItemsDetails } = useSWR(
    selectedProduction?.id && categoryItems
      ? `/expense/test/${selectedProduction.id}/${categoryItems.map((item) => item.id).join(",")}`
      : null,
    async () => {
      const token = localStorage.getItem("token");

      try {
        const itemRequests = categoryItems.map((item) =>
          fetch(
            `${process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT ? '' : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`}/api/expense/test/${selectedProduction.id}/${item.id}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          )
            .then((res) => {
              if (!res.ok) {
                throw new Error(
                  `Error fetching item ${item.id}: ${res.statusText}`
                );
              }
              return res.json();
            })
            .catch((error) => {
              console.error(`Error fetching item ${item.id}:`, error);
              return null; // Return null for this item if the fetch fails
            })
        );

        const results = await Promise.all(itemRequests);

        // Filter out null responses to avoid processing invalid data
        return results.filter((item) => item !== null);
      } catch (error) {
        console.error("Error fetching items:", error);
        throw error; // Let SWR handle the error
      }
    },
    { refreshInterval: 3700 }
  );

  useEffect(() => {
    const storedThreshold = localStorage.getItem("threshold");
    if (storedThreshold) {
      setThreshold(parseFloat(storedThreshold));
    }
  }, []);

  useEffect(() => {
    const savedProduction = localStorage.getItem("selectedProduction");
    if (savedProduction && productions) {
      const parsedProduction = JSON.parse(savedProduction);
      const production = productions.find((p) => p.id === parsedProduction.id);
      if (production) {
        setSelectedProduction(production);
      }
    }
  }, [productions]);

  useEffect(() => {
    if (!selectedProduction?.id || !productions?.length) return;
    const freshProduction = productions.find((p) => p.id === selectedProduction.id);
    if (
      freshProduction &&
      freshProduction.episodes_per_recording !== selectedProduction.episodes_per_recording
    ) {
      setSelectedProduction(freshProduction);
    }
  }, [productions, selectedProduction?.id, selectedProduction?.episodes_per_recording]);

  // Save selected production to localStorage whenever it changes
  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem(
        "selectedProduction",
        JSON.stringify(selectedProduction)
      );
    }
  }, [selectedProduction]);

  const handleCellClick = (row, episodeNumber) => {
    const episodeData = row[`episode-${episodeNumber}`];
    if (!hasEpisodeDisplayData(episodeData)) {
      return;
    }

    setSelectedItem(row.item_db_id);
    setSelectedColumn(episodeNumber);
    setSelectedRecordingEpisodes(null);
    setSelectedRecordingLabel(null);
    setSelectedRecordingEpisodeRange(null);
    setSelectedItemName(row.item);
    setSelectedBudget(row.budget);
  };

  const handleRecordingCellClick = (row, recording) => {
    const field = `recording-${recording.id}`;
    const recordingData = row[field];
    if (
      !recordingData ||
      (recordingData.amount === null && !recordingData.exp_pred)
    ) {
      return;
    }

    const episodes = Array.isArray(recording.episodes)
      ? [...recording.episodes].sort((a, b) => a - b)
      : [];
    if (episodes.length === 0) {
      return;
    }

    setSelectedItem(row.item_db_id);
    setSelectedColumn(null);
    setSelectedRecordingEpisodes(episodes);
    setSelectedRecordingLabel(`Εγγραφή ${recording.rec_number}`);
    setSelectedRecordingEpisodeRange(`(${formatEpisodeRange(episodes)})`);
    setSelectedItemName(row.item);
    setSelectedBudget(row.budget);
  };

  const handleSort = (field) => {
    setSortConfig({
      field,
      direction: sortConfig.field === field && sortConfig.direction === "asc" ? "desc" : "asc",
    });
  };

  const handlePanelClose = () => {
    setSelectedItem(null);
    setSelectedColumn(null);
    setSelectedRecordingEpisodes(null);
    setSelectedRecordingLabel(null);
    setSelectedRecordingEpisodeRange(null);
    setSelectedItemName(null);
  };

  const handleExpenseEditClose = () => {
    setEditExpenseId(null);
    mutateItemsDetails();
    globalMutate("/expense");
  };

  const handleExpenseDetailsEditClose = () => {
    setEditExpenseDetailsId(null);
    mutateItemsDetails();
    globalMutate("/expense");
  };

  const handleProductionChange = (event) => {
    const productionId = Number(event.target.value);
    setSelectedProduction(productions.find((p) => p.id === productionId));
    setThreshold(0.7);
    localStorage.removeItem("threshold");
    // Reset display mode and selected recording when production changes
    setDisplayMode("all");
    setSelectedRecording(null);
    setRecordingMismatchAlertExpanded(true);
  };

  const handleDisplayModeChange = (event) => {
    setDisplayMode(event.target.value);
    if (event.target.value !== "recording") {
      setSelectedRecording(null);
    }
  };

  const handleRecordingChange = (event) => {
    const recordingId = event.target.value;
    const recording = recordings?.find((r) => r.id === Number(recordingId));
    setSelectedRecording(recording || null);
  };

  const handleNextRecording = () => {
    if (!recordings || !selectedRecording) return;
    const currentIndex = recordings.findIndex((r) => r.id === selectedRecording.id);
    if (currentIndex < recordings.length - 1) {
      setSelectedRecording(recordings[currentIndex + 1]);
    }
  };

  const handlePreviousRecording = () => {
    if (!recordings || !selectedRecording) return;
    const currentIndex = recordings.findIndex((r) => r.id === selectedRecording.id);
    if (currentIndex > 0) {
      setSelectedRecording(recordings[currentIndex - 1]);
    }
  };

  const handleThresholdChange = (_, newValue) => {
    setThreshold(newValue);
    localStorage.setItem("threshold", newValue.toString());
  };

  // Calculate which episodes to display based on display mode
  const visibleEpisodes = useMemo(() => {
    if (!selectedProduction || displayMode === "recordings") return [];

    const maxEpisodes = selectedProduction.no_of_episodes;
    const allEpisodes = Array.from({ length: maxEpisodes }, (_, i) => i + 1);

    if (displayMode === "all") {
      return allEpisodes;
    } else if (displayMode === "recording") {
      if (!selectedRecording || !Array.isArray(selectedRecording.episodes)) {
        return [];
      }
      return selectedRecording.episodes.sort((a, b) => a - b);
    } else if (displayMode === "unlocked") {
      const lockedEpisodes = Array.isArray(selectedProduction.locked_episodes)
        ? selectedProduction.locked_episodes
        : [];
      return allEpisodes.filter((ep) => !lockedEpisodes.includes(ep));
    }
    return allEpisodes;
  }, [selectedProduction, displayMode, selectedRecording]);

  const visibleRecordings = useMemo(() => {
    if (displayMode !== "recordings" || !recordings) return [];
    return [...recordings].sort((a, b) => a.rec_number - b.rec_number);
  }, [displayMode, recordings]);

  const isRecordingsView = displayMode === "recordings";
  const showProductionTotals =
    displayMode === "recording" || displayMode === "unlocked" || displayMode === "recordings";

  const expectedEpisodesPerRecording = useMemo(
    () =>
      getExpectedEpisodesPerRecording(
        selectedProduction?.no_of_episodes,
        recordings?.length,
        selectedProduction?.episodes_per_recording
          ? Number(selectedProduction.episodes_per_recording)
          : null
      ),
    [selectedProduction, recordings]
  );

  // When planned episodes/recording is set on production, episode counts per
  // recording may legitimately vary — only use the value for budget hints.
  const showRecordingEpisodeMismatch = !selectedProduction?.episodes_per_recording;

  const recordingsWithEpisodeMismatch = useMemo(() => {
    if (
      !showRecordingEpisodeMismatch ||
      !expectedEpisodesPerRecording ||
      !recordings?.length
    ) {
      return [];
    }
    return recordings.filter((recording) => {
      const count = Array.isArray(recording.episodes) ? recording.episodes.length : 0;
      return hasRecordingEpisodeCountMismatch(count, expectedEpisodesPerRecording);
    });
  }, [recordings, expectedEpisodesPerRecording, showRecordingEpisodeMismatch]);

  const hasPerRecordingBillingItems = useMemo(
    () =>
      categoryItems?.some((item) =>
        isPerRecordingBillingType(item.pci_type, billingTypes)
      ) ?? false,
    [categoryItems, billingTypes]
  );

  const { rows, columnGroupingModel, sortedRows } = useMemo(() => {
    if (!selectedProduction || !categoryItems || !itemsDetails)
      return { columns: [], rows: [], columnGroupingModel: [], sortedRows: [] };

    const maxEpisodes = selectedProduction.no_of_episodes;
    const totalEpisodesInRecordings = visibleRecordings.reduce(
      (sum, rec) => sum + (Array.isArray(rec.episodes) ? rec.episodes.length : 0),
      0
    );

    const gridRows = categoryItems.map((item) => {
      const itemDetailsList = itemsDetails
        .flat()
        .filter((detail) => detail.pci_id === item.id);

      const episodeData = Object.fromEntries(
        Array.from({ length: maxEpisodes }, (_, i) => [
          `episode-${i + 1}`,
          { amount: null, expPred: false, maxEpAm: 0 },
        ])
      );

      let budget = "";

      const categoryItem = categoryItems.find(
        (category) => category.id === item.id
      );

      if (categoryItem) {
        budget = categoryItem.pci_amount;
      }

      itemDetailsList.forEach((itemDetail) => {
        itemDetail.exp_assigned_episodes.forEach((assignedEpisode) => {
          const episodeKey = `episode-${assignedEpisode}`;

          if (itemDetail.exp_pred === true) {
            episodeData[episodeKey].expPred = true;
          }

          const expenses = itemDetail.expense_details.filter(
            (expense) => expense.exp_d_episode === assignedEpisode
          );

          const totalExpense = expenses.reduce(
            (sum, expense) => sum + (expense.exp_d_amount || 0),
            0
          );

          episodeData[episodeKey].amount = (episodeData[episodeKey].amount || 0) + totalExpense;

          if (itemDetail.exp_assigned_episodes?.length === maxEpisodes) {
            episodeData[episodeKey].maxEpAm += totalExpense;
          }
        });
      });

      Object.values(episodeData).forEach((ep) => {
        if (ep.expPred && ep.amount === null) {
          ep.amount = 0;
        }
      });

      const recordingData = isRecordingsView
        ? Object.fromEntries(
            visibleRecordings.map((recording) => {
              const avgData = getRecordingAvgData(episodeData, recording);
              return [
                `recording-${recording.id}`,
                {
                  amount: avgData.amount,
                  exp_pred: avgData.exp_pred,
                  totalAmount: avgData.totalAmount,
                },
              ];
            })
          )
        : {};

      let totalCost;
      let budgetTotal;
      let averageCost;

      if (isRecordingsView) {
        totalCost = visibleRecordings.reduce(
          (sum, recording) =>
            sum + (recordingData[`recording-${recording.id}`]?.totalAmount || 0),
          0
        );
        budgetTotal = budget * totalEpisodesInRecordings;
        const validRecordingCount = visibleRecordings.filter(
          (recording) => recordingData[`recording-${recording.id}`]?.amount !== null
        ).length;
        averageCost =
          validRecordingCount > 0
            ? visibleRecordings.reduce(
                (sum, recording) =>
                  sum + (recordingData[`recording-${recording.id}`]?.amount || 0),
                0
              ) / validRecordingCount
            : 0;
      } else {
        totalCost = visibleEpisodes.reduce((sum, episodeNumber) => {
          const episodeKey = `episode-${episodeNumber}`;
          const episodeValue = episodeData[episodeKey];
          return sum + (episodeValue?.amount || 0);
        }, 0);
        budgetTotal = budget * visibleEpisodes.length;
        const validEpisodeCount = visibleEpisodes.filter((episodeNumber) => {
          const episodeKey = `episode-${episodeNumber}`;
          return episodeData[episodeKey]?.amount !== null;
        }).length;
        averageCost =
          validEpisodeCount > 0 ? totalCost / validEpisodeCount : 0;
      }

      const totalCostProduction = Array.from({ length: maxEpisodes }, (_, i) => i + 1).reduce(
        (sum, episodeNumber) => {
          const episodeKey = `episode-${episodeNumber}`;
          const episodeValue = episodeData[episodeKey];
          return sum + (episodeValue?.amount || 0);
        },
        0
      );

      return {
        item_db_id: item.id,
        id: item.id,
        item: item.pci_descr,
        pci_type: item.pci_type,
        budget,
        budgetTotal,
        budgetTotalProduction: budget * maxEpisodes,
        average: averageCost,
        totalItemActualCost: totalCost,
        totalItemActualCostProduction: totalCostProduction,
        ...Object.fromEntries(
          Object.entries(episodeData).map(([key, value]) => [
            key,
            {
              amount: value.amount,
              exp_pred: value.expPred,
              maxEpAm: value.maxEpAm,
            },
          ])
        ),
        ...recordingData,
      };
    });

    const accumulatedBudget = categoryItems.reduce(
      (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
      0
    );

    const totalItemActualCostAllRows = gridRows.reduce(
      (sum, row) => sum + (parseFloat(row.totalItemActualCost) || 0),
      0
    );
    
    // Calculate production totals (for all episodes)
    const totalItemActualCostProductionAllRows = gridRows.reduce(
      (sum, row) => sum + (parseFloat(row.totalItemActualCostProduction) || 0),
      0
    );
    
    const budgetTotalProductionAllRows = gridRows.reduce(
      (sum, row) => sum + (parseFloat(row.budgetTotalProduction) || 0),
      0
    );

    const columnGroupingModel = [
      {
        groupId: "unamortized",
        headerAlign: "center",
        headerName: "Αναπόσβεστη Αξία",
        description: "",
        children: [
          {
            groupId: "total",
            headerAlign: "center",
            headerName: "Σύνολο",
            children: [{ field: "item" }],
          },
          {
            groupId: "budget",
            headerName: (() => {
              return new Intl.NumberFormat("en-IE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(accumulatedBudget);
            })(),
            description: "Total Budget",
            headerAlign: "right",
            children: [{ field: "budget" }],
          },
          {
            groupId: "average",
            headerName: "",
            description: "",
            headerAlign: "right",
            children: [{ field: "average" }],
          },
        ],
      },
      {
        groupId: `Ep. ${maxEpisodes + 1}`,
        headerAlign: "right",
        headerName: "",
        description: "",
        children: [
          {
            groupId: "budgetTotal",
            headerAlign: "right",
            headerName: new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(accumulatedBudget * (isRecordingsView ? totalEpisodesInRecordings : visibleEpisodes.length)),
            description: displayMode === "recording" ? "Σύνολο Προϋπ. Εγγραφής" 
              : displayMode === "unlocked" ? "Σύνολο Προϋπ. (Unlocked)" 
              : displayMode === "recordings" ? "Σύνολο Προϋπ. (Εγγραφές)"
              : "Σύνολο Προϋπ.",
            children: [{ field: "budgetTotal" }],
          },
        ],
      },
      {
        groupId: `Ep. ${maxEpisodes + 2}`,
        headerAlign: "right",
        headerName: "",
        description: "",
        children: [
          {
            groupId: "totalItemActualCost",
            headerAlign: "right",
            headerName: new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(totalItemActualCostAllRows),
            description: displayMode === "recording" ? "Σύνολο Δαπάνης Εγγραφής" 
              : displayMode === "unlocked" ? "Σύνολο Δαπάνης (Unlocked)" 
              : displayMode === "recordings" ? "Σύνολο Δαπάνης (Εγγραφές)"
              : "Σύνολο Δαπάνης",
            children: [{ field: "totalItemActualCost" }],
          },
        ],
      },
      ...(showProductionTotals ? [
        {
          groupId: `Ep. ${maxEpisodes + 3}`,
          headerAlign: "right",
          headerName: "",
          description: "",
          children: [
            {
              groupId: "budgetTotalProduction",
              headerAlign: "right",
              headerName: new Intl.NumberFormat("en-IE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(accumulatedBudget * maxEpisodes),
              description: "Σύνολο Προϋπ. Παραγωγής",
              children: [{ field: "budgetTotalProduction" }],
            },
          ],
        },
        {
          groupId: `Ep. ${maxEpisodes + 4}`,
          headerAlign: "right",
          headerName: "",
          description: "",
          children: [
            {
              groupId: "totalItemActualCostProduction",
              headerAlign: "right",
              headerName: new Intl.NumberFormat("en-IE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(totalItemActualCostProductionAllRows),
              description: "Σύνολο Δαπάνης Παραγωγής",
              children: [{ field: "totalItemActualCostProduction" }],
            },
          ],
        },
      ] : []),
      ...(isRecordingsView
        ? visibleRecordings.map((recording) => {
            const field = `recording-${recording.id}`;
            const lastEpisode = getLastEpisodeOfRecording(recording);
            const projectionValue = getEpisodeUnamortizedValue(
              lastEpisode,
              gridRows,
              maxEpisodes
            );
            const formattedProjection = new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(projectionValue);
            const recordingAvgSum = gridRows.reduce(
              (sum, row) => sum + parseFloat(row[field]?.amount || 0),
              0
            );
            const formattedRecordingSum = new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(recordingAvgSum);

            const isExceeded = recordingAvgSum > accumulatedBudget;
            const recordingSumStyle = isExceeded
              ? { color: "red" }
              : { color: "#2bc12b" };

            return {
              groupId: `Rec. ${recording.rec_number}`,
              headerAlign: "right",
              headerName: formattedProjection,
              description: "",
              children: [
                {
                  groupId: `Recording ${recording.rec_number}`,
                  headerAlign: "right",
                  headerName: (
                    <span style={recordingSumStyle}>{formattedRecordingSum}</span>
                  ),
                  description: recording.rec_name || "",
                  children: [{ field }],
                },
              ],
            };
          })
        : visibleEpisodes.map((episodeNumber) => {
        const episodeSum = gridRows.reduce(
          (sum, row) => sum + parseFloat(row[`episode-${episodeNumber}`]?.amount || 0),
          0
        );

        const totalOfFullAssigned = gridRows.reduce(
          (sum, row) =>
            sum + (parseFloat(row[`episode-${episodeNumber}`]?.maxEpAm) || 0),
          0
        );

        const projectionValue = totalOfFullAssigned * (maxEpisodes - episodeNumber + 1); //* (maxEpisodes - i)
        const formattedProjection = new Intl.NumberFormat("en-IE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(projectionValue);

        const formattedEpisodeSum = new Intl.NumberFormat("en-IE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(episodeSum);

        const isExceeded = episodeSum > accumulatedBudget;
        const episodeSumStyle = isExceeded
          ? { color: "red" }
          : { color: "#2bc12b" };

        return {
          groupId: `Ep. ${episodeNumber}`,
          headerAlign: "right",
          headerName: formattedProjection,
          description: "",
          children: [
            {
              groupId: `Episode ${episodeNumber}`,
              headerAlign: "right",
              headerName: (
                <span style={episodeSumStyle}>{formattedEpisodeSum}</span>
              ),
              description: ` `,
              children: [{ field: `episode-${episodeNumber}` }],
            },
          ],
        };
      })),
    ];

    // Sort rows
    const sorted = [...gridRows].sort((a, b) => {
      const aVal = a[sortConfig.field];
      const bVal = b[sortConfig.field];
      
      if (sortConfig.field.startsWith('episode-') || sortConfig.field.startsWith('recording-')) {
        const aAmount = aVal?.amount || 0;
        const bAmount = bVal?.amount || 0;
        return sortConfig.direction === "asc" ? aAmount - bAmount : bAmount - aAmount;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === "asc" 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      const aNum = parseFloat(aVal) || 0;
      const bNum = parseFloat(bVal) || 0;
      return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
    });

    return {
      rows: [...gridRows],
      sortedRows: sorted,
      columnGroupingModel,
    };
  }, [selectedProduction, categoryItems, itemsDetails, sortConfig, visibleEpisodes, visibleRecordings, displayMode, selectedRecording, isRecordingsView, showProductionTotals]);

  const isLoading =
    selectedProduction &&
    (!categoryItems || !itemsDetails || (isRecordingsView && recordings === undefined));

  if (productionsError || categoryError || detailsError) {
    return <Typography>Error loading data.</Typography>;
  }

  const getColumnGroupLeaves = (group, callback) => {
    const traverse = (node) => {
      if (node.children) {
        node.children.forEach((child) => {
          if (child.field) {
        callback(child.field);
      } else {
            traverse(child);
          }
        });
      }
    };
    traverse(group);
  };

  const toggleColumnVisibility = (field) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const toggleColumnGroup = (group, checked) => {
      const fields = [];
      getColumnGroupLeaves(group, (field) => fields.push(field));
    setColumnVisibility((prev) => {
      const newState = { ...prev };
      fields.forEach((field) => {
        newState[field] = !checked;
      });
      return newState;
    });
  };

  const isColumnVisible = (field) => {
    return columnVisibility[field] !== false;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return null;
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const episodeCellSx = {
    borderRight: "1px solid #ddd",
    borderBottom: "none",
    padding: "8px 8px",
  };

  const renderEmptyEpisodeCell = (key) => (
    <TableCell key={key} align="right" sx={episodeCellSx} />
  );

  const renderRecordingCell = (row, recording) => {
    const field = `recording-${recording.id}`;
    const recordingData = row[field];

    if (
      !recordingData ||
      (recordingData.amount === null && !recordingData.exp_pred)
    ) {
      return renderEmptyEpisodeCell(field);
    }

    const amount = recordingData.amount ?? 0;
    const { exp_pred } = recordingData;
    const budget = parseFloat(row.budget);
    const isAboveBudget = amount > budget;
    const isBelowThreshold = !isAboveBudget && amount < budget * threshold;

    const recordingMismatch =
      showRecordingEpisodeMismatch &&
      isPerRecordingBillingType(row.pci_type, billingTypes) &&
      getRecordingBudgetMismatch({
        recording,
        pciAmount: budget,
        currentPerEpisodeAmount: amount,
        expectedEpisodesPerRecording,
        hasPrediction: exp_pred,
      });

    const formattedAmount = formatCurrency(amount);

    return (
      <TableCell
        key={field}
        align="right"
        onClick={() => handleRecordingCellClick(row, recording)}
        sx={{
          cursor: "pointer",
          color: isAboveBudget ? "red" : isBelowThreshold ? "orange" : "inherit",
          fontWeight: isAboveBudget || isBelowThreshold ? "bold" : "inherit",
          position: "relative",
          borderRight: "1px solid #ddd",
          borderBottom: "none",
          padding: "8px 8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {exp_pred && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: "98.5px",
                width: 0,
                height: 0,
                borderRight: "10px solid transparent",
                borderTop: "10px solid green",
                zIndex: 1,
              }}
            />
          )}
          {amount !== null && isBelowThreshold && !isAboveBudget && (
            <WarningIcon fontSize="small" sx={{ color: "orange" }} />
          )}
          {recordingMismatch?.needsAttention && (
            <Tooltip
              title={formatRecordingMismatchTooltip(recordingMismatch, formatCurrency)}
            >
              <WarningAmberIcon fontSize="small" sx={{ color: "#ed6c02" }} />
            </Tooltip>
          )}
          {formattedAmount}
        </div>
      </TableCell>
    );
  };

  const renderEpisodeCell = (row, episodeNumber) => {
    const episodeData = row[`episode-${episodeNumber}`];
    const field = `episode-${episodeNumber}`;

    if (!hasEpisodeDisplayData(episodeData)) {
      return renderEmptyEpisodeCell(field);
    }

    const amount = episodeData.amount ?? 0;
    const { exp_pred } = episodeData;
    const budget = parseFloat(row.budget);
    const isAboveBudget = amount > budget;
    const isBelowThreshold = !isAboveBudget && amount < budget * threshold;

    const formattedAmount = formatCurrency(amount);

    return (
      <TableCell
        key={`episode-${episodeNumber}`}
        align="right"
        onClick={() => handleCellClick(row, episodeNumber)}
        sx={{
          cursor: "pointer",
          color: isAboveBudget ? "red" : isBelowThreshold ? "orange" : "inherit",
          fontWeight: isAboveBudget || isBelowThreshold ? "bold" : "inherit",
          position: "relative",
          borderRight: "1px solid #ddd",
          borderBottom: "none",
          padding: "8px 8px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {exp_pred && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: "98.5px",
                width: 0,
                height: 0,
                borderRight: "10px solid transparent",
                borderTop: "10px solid green",
                zIndex: 1,
              }}
            />
          )}
          {amount !== null && isBelowThreshold && !isAboveBudget && (
            <WarningIcon fontSize="small" sx={{ color: "orange" }} />
          )}
          {formattedAmount}
        </div>
      </TableCell>
    );
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="Category Items"
          module="Productions"
          subtitle="List of category items"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>
          <Box m="8px 0 0 0" height="80vh">
            <Paper
              sx={{
                padding: "1rem",
                height: "82vh",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ flexShrink: 0, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: "200px" }}>
                  <InputLabel id="prod-label">Παραγωγή</InputLabel>
                  <Select
                    labelId="prod-label"
                    id="prod-id"
                    label="Production"
                    size="small"
                    value={selectedProduction?.id || ""}
                    onChange={handleProductionChange}
                  >
                    {productions?.map((production) => (
                      <MenuItem key={production.id} value={production.id}>
                        {production.p_descr}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Display Mode Selection */}
                {selectedProduction && (
                  <>
                    <Box>
                      <RadioGroup
                        row
                        value={displayMode}
                        onChange={handleDisplayModeChange}
                      >
                        <FormControlLabel value="all" control={<Radio />} label="Όλα τα Επεισόδια" />
                        <FormControlLabel value="recording" control={<Radio />} label="Επιλεγμένη Εγγραφή" />
                        <FormControlLabel value="unlocked" control={<Radio />} label="Μόνο Ξεκλείδωτα" />
                        <FormControlLabel value="recordings" control={<Radio />} label="Εγγραφές" />
                      </RadioGroup>
                    </Box>
                    
                    {/* Recording Selection (only shown when mode is "recording") */}
                    {displayMode === "recording" && recordings && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: "300px" }}>
                          <InputLabel id="recording-label">Εγγραφή</InputLabel>
                          <Select
                            labelId="recording-label"
                            id="recording-select"
                            label="Εγγραφή"
                            value={selectedRecording?.id || ""}
                            onChange={handleRecordingChange}
                          >
                            {recordings.length === 0 ? (
                              <MenuItem disabled value="">
                                <em>Δεν υπάρχουν εγγραφές</em>
                              </MenuItem>
                            ) : (
                              recordings.map((recording) => {
                                const hasMismatch =
                                  showRecordingEpisodeMismatch &&
                                  expectedEpisodesPerRecording &&
                                  hasRecordingEpisodeCountMismatch(
                                    recording.episodes?.length || 0,
                                    expectedEpisodesPerRecording
                                  );
                                return (
                                <MenuItem key={recording.id} value={recording.id}>
                                  {hasMismatch && (
                                    <WarningAmberIcon
                                      fontSize="small"
                                      sx={{ color: "#ed6c02", mr: 1 }}
                                    />
                                  )}
                                  Εγγραφή {recording.rec_number}
                                  {recording.rec_name && ` - ${recording.rec_name}`}
                                  {recording.episodes && recording.episodes.length > 0 && (
                                    ` (${formatEpisodeRange(recording.episodes)})`
                                  )}
                                </MenuItem>
                              );
                              })
                            )}
                          </Select>
                        </FormControl>
                        {selectedRecording && recordings.length > 1 && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ArrowBackIcon />}
                              onClick={handlePreviousRecording}
                              disabled={recordings.findIndex((r) => r.id === selectedRecording.id) === 0}
                            >
                              Προηγούμενη
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              endIcon={<ArrowForwardIcon />}
                              onClick={handleNextRecording}
                              disabled={recordings.findIndex((r) => r.id === selectedRecording.id) === recordings.length - 1}
                            >
                              Επόμενη
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>

              <Box display="flex" alignItems="center" mb={2} sx={{ flexShrink: 0 }}>
                <Typography>Όριο:</Typography>
                <Slider
                  value={threshold}
                  min={0.1}
                  max={1}
                  step={0.05}
                  onChange={handleThresholdChange}
                  valueLabelDisplay="auto"
                  style={{ marginLeft: "16px", width: "200px" }}
                />
                <Typography>{Math.round(threshold * 100)}%</Typography>
              </Box>

              {/* Show loading indicator only after a production is selected */}
              {isLoading && <LinearProgress sx={{ flexShrink: 0 }} />}

              {isRecordingsView &&
                hasPerRecordingBillingItems &&
                recordingsWithEpisodeMismatch.length > 0 && (
                <Alert
                  severity="warning"
                  sx={{
                    mb: 2,
                    flexShrink: 0,
                    minWidth: 0,
                    maxWidth: "100%",
                    alignItems: "flex-start",
                    "& .MuiAlert-message": {
                      width: "100%",
                      minWidth: 0,
                      overflow: "hidden",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() =>
                      setRecordingMismatchAlertExpanded((prev) => !prev)
                    }
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                      Αναντιστοιχία εγγραφών με την αρχική πρόβλεψη (
                      {recordingsWithEpisodeMismatch.length}{" "}
                      {recordingsWithEpisodeMismatch.length === 1
                        ? "εγγραφή"
                        : "εγγραφές"}
                      )
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={
                        recordingMismatchAlertExpanded ? "Σύμπτυξη" : "Ανάπτυξη"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setRecordingMismatchAlertExpanded((prev) => !prev);
                      }}
                      sx={{ p: 0.25, ml: 0.5 }}
                    >
                      {recordingMismatchAlertExpanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse in={recordingMismatchAlertExpanded}>
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Αναμενόμενα ~{expectedEpisodesPerRecording} επεισόδια ανά εγγραφή
                        {selectedProduction?.episodes_per_recording
                          ? " (σύμφωνα με σχεδιασμό παραγωγής)"
                          : ` (${selectedProduction?.no_of_episodes} επ. / ${recordings?.length} εγγραφές)`}
                        . Οι παρακάτω εγγραφές έχουν διαφορετικό αριθμό — ελέγξτε τις
                        προβλέψεις:
                      </Typography>
                      {recordingsWithEpisodeMismatch.map((recording) => (
                        <Typography key={recording.id} variant="body2">
                          • Εγγραφή {recording.rec_number}:{" "}
                          {Array.isArray(recording.episodes)
                            ? recording.episodes.length
                            : 0}{" "}
                          επ.
                          {recording.episodes?.length > 0 &&
                            ` (${formatEpisodeRange(recording.episodes)})`}
                        </Typography>
                      ))}
                      <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                        Ισχύει για είδη δαπάνης με τρόπο τιμολόγησης «Ανά εγγραφή».
                        Τα κελιά με προειδοποίηση δείχνουν προτεινόμενο ποσό/επ. βάσει
                        πραγματικών επεισοδίων της εγγραφής.
                      </Typography>
                    </Box>
                  </Collapse>
                </Alert>
              )}

              {displayMode === "recording" &&
                showRecordingEpisodeMismatch &&
                selectedRecording &&
                expectedEpisodesPerRecording &&
                hasRecordingEpisodeCountMismatch(
                  selectedRecording.episodes?.length || 0,
                  expectedEpisodesPerRecording
                ) && (
                  <Alert severity="warning" sx={{ mb: 2, flexShrink: 0, minWidth: 0, maxWidth: "100%" }}>
                    <Typography variant="body2">
                      Η Εγγραφή {selectedRecording.rec_number} έχει{" "}
                      {selectedRecording.episodes?.length || 0} επεισόδια (αναμενόμενα ~
                      {expectedEpisodesPerRecording}). Ελέγξτε αν οι προβλέψεις χρειάζονται
                      προσαρμογή στο κόστος ανά επεισόδιο.
                    </Typography>
                  </Alert>
                )}

              </Box>

              {/*If no prod. is selected yet*/}
              {!selectedProduction && (
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "70vh" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #ddd" }}>Στοιχεία</TableCell>
                        <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #ddd" }}>Προϋπ.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={2} align="center">Επιλέξτε μια παραγωγή</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/*Table*/}
              {selectedProduction && categoryItems && !isLoading && (
                <TableContainer 
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    overflow: "auto",
                    overflowX: "auto",
                    "& .MuiTableCell-root": {
                      borderRight: "1px solid #ddd",
                      borderBottom: "none",
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      borderRight: "1px solid #ddd",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <Table stickyHeader sx={{ minWidth: "max-content" }}>
                    <TableHead
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 12,
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {/* Level 1 Headers - Top Row */}
                      <TableRow
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 12,
                        }}
                      >
                        {/* Unamortized header spanning 3 columns (Στοιχείο, Προϋπολογισμός, Μέσος Όρος) */}
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: 0,
                            top: 0,
                            zIndex: 13,
                            minWidth: "200px",
                          }}
                        >
                          {columnGroupingModel.find(g => g.groupId === "unamortized")?.headerName}
                        </TableCell>
                        {/* Episode / Recording headers in order */}
                        {isRecordingsView
                          ? visibleRecordings.map((recording) => {
                              const field = `recording-${recording.id}`;
                              if (!isColumnVisible(field)) return null;

                              const lastEpisode = getLastEpisodeOfRecording(recording);
                              const projectionValue = getEpisodeUnamortizedValue(
                                lastEpisode,
                                rows,
                                selectedProduction.no_of_episodes
                              );
                              const formattedProjection = formatCurrency(projectionValue);

                              return (
                                <TableCell
                                  key={`rec-level1-${recording.id}`}
                                  colSpan={1}
                                  rowSpan={1}
                                  align="right"
                                  sx={{
                                    fontWeight: "bold",
                                    borderRight: "1px solid #ddd",
                                    borderBottom: "1px solid #ddd",
                                    backgroundColor: "#f5f5f5",
                                    padding: "8px 16px",
                                  }}
                                >
                                  {formattedProjection}
                                </TableCell>
                              );
                            })
                          : visibleEpisodes.map((episodeNumber) => {
                          const field = `episode-${episodeNumber}`;
                          if (!isColumnVisible(field)) return null;
                          
                          // Calculate projection value
                          const totalOfFullAssigned = rows.reduce(
                            (sum, row) => sum + (parseFloat(row[field]?.maxEpAm) || 0),
                            0
                          );
                          const projectionValue = totalOfFullAssigned * (selectedProduction.no_of_episodes - episodeNumber + 1);
                          const formattedProjection = formatCurrency(projectionValue);
                          
                          return (
                            <TableCell
                              key={`ep-level1-${episodeNumber}`}
                              colSpan={1}
                              rowSpan={1}
                              align="right"
                              sx={{
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {formattedProjection}
                            </TableCell>
                          );
                        })}
                        {/* Budget Total header */}
                        {isColumnVisible("budgetTotal") && (
                          <TableCell
                            key="budget-total-level1"
                            colSpan={1}
                            rowSpan={1}
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                      borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                          </TableCell>
                        )}
                        {/* Total Actual Cost header */}
                        {isColumnVisible("totalItemActualCost") && (
                          <TableCell
                            key="total-cost-level1"
                            colSpan={1}
                            rowSpan={1}
                            align="center"
                            sx={{ 
                      fontWeight: "bold",
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                          </TableCell>
                        )}
                        {/* Production Total Budget header - Level 1 (only for recording/unlocked) */}
                        {(showProductionTotals) && isColumnVisible("budgetTotalProduction") && (
                          <TableCell
                            key="budget-total-production-level1"
                            colSpan={1}
                            rowSpan={1}
                            align="center"
                            sx={{ 
                              fontWeight: "bold",
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                          </TableCell>
                        )}
                        {/* Production Total Actual Cost header - Level 1 (only for recording/unlocked) */}
                        {(showProductionTotals) && isColumnVisible("totalItemActualCostProduction") && (
                          <TableCell
                            key="total-cost-production-level1"
                            colSpan={1}
                            rowSpan={1}
                            align="center"
                            sx={{ 
                              fontWeight: "bold",
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 16px",
                            }}
                          >
                          </TableCell>
                        )}
                        {/* Empty column header - Level 1 */}
                        <TableCell
                          key="empty-level1"
                          colSpan={1}
                          rowSpan={1}
                          align="center"
                          sx={{ 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            minWidth: "50px",
                            width: "50px",
                          }}
                        >
                        </TableCell>
                      </TableRow>
                      {/* Level 2 Headers - Middle Row */}
                      <TableRow
                        sx={{
                          position: "sticky",
                          top: "48px", // Height of Level 1 row
                          zIndex: 11,
                        }}
                      >
                        {/* Σύνολο - Item column (empty) */}
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: 0,
                            top: "48px",
                            zIndex: 12,
                            minWidth: "200px",
                            maxWidth: "250px",
                            width: "200px",
                          }}
                        >
                          Σύνολο
                        </TableCell>
                        {/* Budget Total */}
                        <TableCell
                          align="right"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: "200px",
                            top: "48px",
                            zIndex: 12,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          {formatCurrency(
                            categoryItems?.reduce(
                              (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
                              0
                            ) || 0
                          )}
                        </TableCell>
                        {/* Average Total */}
                        <TableCell
                          align="right"
                          sx={{ 
                            fontWeight: "bold",
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            position: "sticky",
                            left: "300px",
                            top: "48px",
                            zIndex: 12,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          {formatCurrency(
                            rows.reduce((sum, row) => sum + (parseFloat(row.average) || 0), 0)
                          )}
                        </TableCell>
                        {/* Episode / Recording totals in order */}
                        {isRecordingsView
                          ? visibleRecordings.map((recording) => {
                              const field = `recording-${recording.id}`;
                              if (!isColumnVisible(field)) return null;

                              const recordingAvgSum = rows.reduce(
                                (sum, row) => sum + parseFloat(row[field]?.amount || 0),
                                0
                              );
                              const accumulatedBudget = categoryItems?.reduce(
                                (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
                                0
                              ) || 0;
                              const isExceeded = recordingAvgSum > accumulatedBudget;
                              const recordingColor = isExceeded ? "red" : "#2bc12b";
                              const formattedRecordingSum = formatCurrency(recordingAvgSum);

                              return (
                                <TableCell
                                  key={`rec-level2-${recording.id}`}
                                  colSpan={1}
                                  align="right"
                                  sx={{
                                    fontWeight: "bold",
                                    borderRight: "1px solid #ddd",
                                    borderBottom: "1px solid #ddd",
                                    backgroundColor: "#f5f5f5",
                                    padding: "8px 16px",
                                    color: recordingColor + " !important",
                                  }}
                                >
                                  <span style={{ color: recordingColor }}>
                                    {formattedRecordingSum}
                                  </span>
                                </TableCell>
                              );
                            })
                          : visibleEpisodes.map((episodeNumber) => {
                          const field = `episode-${episodeNumber}`;
                          if (!isColumnVisible(field)) return null;
                          
                          // Calculate episode sum
                          const episodeSum = rows.reduce(
                            (sum, row) => sum + parseFloat(row[field]?.amount || 0),
                            0
                          );
                          const accumulatedBudget = categoryItems?.reduce(
                            (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
                            0
                          ) || 0;
                          const isExceeded = episodeSum > accumulatedBudget;
                          const episodeColor = isExceeded ? "red" : "#2bc12b";
                          const formattedEpisodeSum = formatCurrency(episodeSum);

                        return (
                            <TableCell
                              key={`ep-level2-${episodeNumber}`}
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                                color: episodeColor + " !important",
                              }}
                            >
                              <span style={{ color: episodeColor }}>
                                {formattedEpisodeSum}
                              </span>
                            </TableCell>
                          );
                        })}
                        {/* Budget Total value */}
                        {isColumnVisible("budgetTotal") && (() => {
                          const budgetTotalGroup = columnGroupingModel.find(
                            (g) => g.groupId === `Ep. ${selectedProduction.no_of_episodes + 1}`
                          );
                          const headerContent = budgetTotalGroup?.headerName || "";
                        return (
                            <TableCell
                              key="budget-total-level2"
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {typeof headerContent === "string" ? headerContent : ""}
                            </TableCell>
                          );
                        })()}
                        {/* Total Actual Cost value */}
                        {isColumnVisible("totalItemActualCost") && (() => {
                          const totalCostGroup = columnGroupingModel.find(
                            (g) => g.groupId === `Ep. ${selectedProduction.no_of_episodes + 2}`
                          );
                          const headerContent = totalCostGroup?.headerName || "";
                          return (
                            <TableCell
                              key="total-cost-level2"
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {typeof headerContent === "string" ? headerContent : ""}
                            </TableCell>
                          );
                        })()}
                        {/* Production Total Budget - Level 2 (only for recording/unlocked) */}
                        {(showProductionTotals) && isColumnVisible("budgetTotalProduction") && (() => {
                          const budgetTotalProductionGroup = columnGroupingModel.find(
                            (g) => g.groupId === `Ep. ${selectedProduction.no_of_episodes + 3}`
                          );
                          const headerContent = budgetTotalProductionGroup?.headerName || "";
                          return (
                            <TableCell
                              key="budget-total-production-level2"
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {typeof headerContent === "string" ? headerContent : ""}
                            </TableCell>
                          );
                        })()}
                        {/* Production Total Actual Cost - Level 2 (only for recording/unlocked) */}
                        {(showProductionTotals) && isColumnVisible("totalItemActualCostProduction") && (() => {
                          const totalCostProductionGroup = columnGroupingModel.find(
                            (g) => g.groupId === `Ep. ${selectedProduction.no_of_episodes + 4}`
                          );
                          const headerContent = totalCostProductionGroup?.headerName || "";
                          return (
                            <TableCell
                              key="total-cost-production-level2"
                              colSpan={1}
                              align="right"
                              sx={{ 
                                fontWeight: "bold", 
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 16px",
                              }}
                            >
                              {typeof headerContent === "string" ? headerContent : ""}
                            </TableCell>
                          );
                        })()}
                        {/* Empty column header - Level 2 */}
                        <TableCell
                          key="empty-level2"
                          colSpan={1}
                          align="center"
                          sx={{ 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            minWidth: "50px",
                            width: "50px",
                          }}
                        >
                        </TableCell>
                      </TableRow>
                      {/* Level 3 Headers - Bottom Row */}
                      <TableRow
                        sx={{
                          position: "sticky",
                          top: "96px", // Height of Level 1 + Level 2 rows
                          zIndex: 10,
                        }}
                      >
                        {/* Level 3: Column headers - Sticky columns */}
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold", 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            display: isColumnVisible("item") ? "table-cell" : "none",
                            padding: "8px 12px",
                            position: "sticky",
                            left: 0,
                            top: "96px",
                            zIndex: 11,
                            minWidth: "200px",
                            maxWidth: "250px",
                            width: "200px",
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.field === "item"}
                            direction={sortConfig.field === "item" ? sortConfig.direction : "asc"}
                            onClick={() => handleSort("item")}
                          >
                            Στοιχείο
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold", 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            display: isColumnVisible("budget") ? "table-cell" : "none",
                            padding: "8px 12px",
                            position: "sticky",
                            left: "200px",
                            top: "96px",
                            zIndex: 11,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.field === "budget"}
                            direction={sortConfig.field === "budget" ? sortConfig.direction : "asc"}
                            onClick={() => handleSort("budget")}
                          >
                            Προϋπ.
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ 
                            fontWeight: "bold", 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            display: isColumnVisible("average") ? "table-cell" : "none",
                            padding: "8px 12px",
                            position: "sticky",
                            left: "300px",
                            top: "96px",
                            zIndex: 11,
                            minWidth: "100px",
                            maxWidth: "120px",
                            width: "100px",
                          }}
                        >
                          <TableSortLabel
                            active={sortConfig.field === "average"}
                            direction={sortConfig.field === "average" ? sortConfig.direction : "asc"}
                            onClick={() => handleSort("average")}
                          >
                            Μέσος Όρος
                          </TableSortLabel>
                        </TableCell>
                        {/* Episode / Recording columns - Level 3 */}
                        {isRecordingsView
                          ? visibleRecordings.map((recording) => {
                              const field = `recording-${recording.id}`;
                              if (!isColumnVisible(field)) return null;

                              return (
                                <TableCell
                                  key={`rec-level3-${recording.id}`}
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    borderRight: "1px solid #ddd",
                                    borderBottom: "1px solid #ddd",
                                    backgroundColor: "#f5f5f5",
                                    minWidth: 120,
                                    width: 120,
                                    padding: "8px 8px",
                                  }}
                                >
                                  <TableSortLabel
                                    active={sortConfig.field === field}
                                    direction={sortConfig.field === field ? sortConfig.direction : "asc"}
                                    onClick={() => handleSort(field)}
                                    sx={{ justifyContent: "center" }}
                                  >
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                                      <span>Εγγραφή {recording.rec_number}</span>
                                      {recording.episodes?.length > 0 && (
                                        <span style={{ fontSize: "0.7rem", fontWeight: "normal" }}>
                                          ({formatEpisodeRange(recording.episodes)})
                                        </span>
                                      )}
                                    </Box>
                                  </TableSortLabel>
                                </TableCell>
                              );
                            })
                          : visibleEpisodes.map((episodeNumber) => {
                        const lockedEpisodes = Array.isArray(selectedProduction?.locked_episodes) 
                          ? selectedProduction.locked_episodes 
                          : [];
                        const isLocked = lockedEpisodes.includes(episodeNumber);
                          const field = `episode-${episodeNumber}`;

                          if (!isColumnVisible(field)) return null;

                          return (
                            <TableCell
                              key={`ep-level3-${episodeNumber}`}
                              align="center"
                              sx={{
                                fontWeight: "bold",
                                borderRight: "1px solid #ddd",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#f5f5f5",
                                minWidth: 100,
                                width: 100,
                                padding: "8px 8px",
                              }}
                            >
                              <TableSortLabel
                                active={sortConfig.field === field}
                                direction={sortConfig.field === field ? sortConfig.direction : "asc"}
                                onClick={() => handleSort(field)}
                                sx={{ justifyContent: "center" }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                  {isLocked && <LockIcon sx={{ fontSize: 16, color: "#ff9800" }} />}
                                  <span>E-{episodeNumber}</span>
                                </Box>
                              </TableSortLabel>
                            </TableCell>
                          );
                        })}
                        {/* Budget Total - Level 3 */}
                        {isColumnVisible("budgetTotal") && (
                          <TableCell
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 8px",
                              minWidth: "120px",
                              maxWidth: "150px",
                              width: "130px",
                            }}
                          >
                            <TableSortLabel
                              active={sortConfig.field === "budgetTotal"}
                              direction={sortConfig.field === "budgetTotal" ? sortConfig.direction : "asc"}
                              onClick={() => handleSort("budgetTotal")}
                            >
                              {displayMode === "recording" ? "Σύνολο Προϋπ. Εγγραφής" 
                                : displayMode === "unlocked" ? "Σύνολο Προϋπ. (Unlocked)" 
                                : displayMode === "recordings" ? "Σύνολο Προϋπ. (Εγγραφές)"
                                : "Σύνολο Προϋπ."}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {/* Total Actual Cost - Level 3 */}
                        {isColumnVisible("totalItemActualCost") && (
                          <TableCell
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 8px",
                              minWidth: "120px",
                              maxWidth: "150px",
                              width: "130px",
                            }}
                          >
                            <TableSortLabel
                              active={sortConfig.field === "totalItemActualCost"}
                              direction={sortConfig.field === "totalItemActualCost" ? sortConfig.direction : "asc"}
                              onClick={() => handleSort("totalItemActualCost")}
                            >
                              {displayMode === "recording" ? "Σύνολο Δαπάνης Εγγραφής" 
                                : displayMode === "unlocked" ? "Σύνολο Δαπάνης (Unlocked)" 
                                : displayMode === "recordings" ? "Σύνολο Δαπάνης (Εγγραφές)"
                                : "Σύνολο Δαπάνης"}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {/* Production Total Budget - Level 3 (only for recording/unlocked) */}
                        {(showProductionTotals) && isColumnVisible("budgetTotalProduction") && (
                          <TableCell
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 8px",
                              minWidth: "120px",
                              maxWidth: "150px",
                              width: "130px",
                            }}
                          >
                            <TableSortLabel
                              active={sortConfig.field === "budgetTotalProduction"}
                              direction={sortConfig.field === "budgetTotalProduction" ? sortConfig.direction : "asc"}
                              onClick={() => handleSort("budgetTotalProduction")}
                            >
                              Σύνολο Προϋπ. Παραγωγής
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {/* Production Total Actual Cost - Level 3 (only for recording/unlocked) */}
                        {(showProductionTotals) && isColumnVisible("totalItemActualCostProduction") && (
                          <TableCell
                            align="center"
                            sx={{ 
                              fontWeight: "bold", 
                              borderRight: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              backgroundColor: "#f5f5f5",
                              padding: "8px 8px",
                              minWidth: "120px",
                              maxWidth: "150px",
                              width: "130px",
                            }}
                          >
                            <TableSortLabel
                              active={sortConfig.field === "totalItemActualCostProduction"}
                              direction={sortConfig.field === "totalItemActualCostProduction" ? sortConfig.direction : "asc"}
                              onClick={() => handleSort("totalItemActualCostProduction")}
                            >
                              Σύνολο Δαπάνης Παραγωγής
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {/* Empty column at the end to reduce total columns width */}
                        <TableCell
                          align="center"
                          sx={{ 
                            borderRight: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            backgroundColor: "#f5f5f5",
                            padding: "8px 16px",
                            minWidth: "50px",
                            width: "50px",
                          }}
                        >
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isRecordingsView && visibleRecordings.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            Δεν υπάρχουν εγγραφές για αυτή την παραγωγή
                          </TableCell>
                        </TableRow>
                      )}
                      {sortedRows.map((row) => (
                        <TableRow 
                          key={row.id} 
                          hover
                          sx={{
                            "& .MuiTableCell-root": {
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              padding: "8px 16px",
                            },
                            "&:hover .MuiTableCell-root": {
                              backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                            },
                            "&:hover .MuiTableCell-root[data-sticky='true']": {
                              backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {/* Item column */}
                          <TableCell
                            data-sticky="true"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("item") ? "table-cell" : "none",
                              position: "sticky",
                              left: 0,
                              zIndex: 9,
                              backgroundColor: "white",
                              minWidth: "200px",
                              maxWidth: "250px",
                              width: "200px",
                              padding: "8px 12px",
                            }}
                          >
                            {row.item}
                          </TableCell>
                          {/* Budget column */}
                          <TableCell
                            data-sticky="true"
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("budget") ? "table-cell" : "none",
                              position: "sticky",
                              left: "200px",
                              zIndex: 9,
                              backgroundColor: "white",
                              minWidth: "100px",
                              maxWidth: "120px",
                              width: "100px",
                              padding: "8px 12px",
                            }}
                          >
                            {formatCurrency(row.budget)}
                          </TableCell>
                          {/* Average column */}
                          <TableCell
                            data-sticky="true"
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("average") ? "table-cell" : "none",
                              position: "sticky",
                              left: "300px",
                              zIndex: 9,
                              backgroundColor: "white",
                              minWidth: "100px",
                              maxWidth: "120px",
                              width: "100px",
                              padding: "8px 12px",
                            }}
                          >
                            {formatCurrency(row.average)}
                          </TableCell>
                          {isRecordingsView
                            ? visibleRecordings.map((recording) => {
                                const field = `recording-${recording.id}`;
                                if (!isColumnVisible(field)) return null;
                                return renderRecordingCell(row, recording);
                              })
                            : visibleEpisodes.map((episodeNumber) => {
                            const field = `episode-${episodeNumber}`;
                            if (!isColumnVisible(field)) return null;
                            return renderEpisodeCell(row, episodeNumber);
                          })}
                          <TableCell
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("budgetTotal") ? "table-cell" : "none",
                              padding: "8px 8px",
                              minWidth: "120px",
                              maxWidth: "150px",
                              width: "130px",
                            }}
                          >
                            {formatCurrency(row.budgetTotal)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              display: isColumnVisible("totalItemActualCost") ? "table-cell" : "none",
                              padding: "8px 8px",
                              minWidth: "120px",
                              maxWidth: "150px",
                              width: "130px",
                            }}
                          >
                            {formatCurrency(row.totalItemActualCost)}
                          </TableCell>
                          {/* Production Total Budget (only for recording/unlocked) */}
                          {(showProductionTotals) && (
                            <TableCell
                              align="right"
                              sx={{ 
                                borderRight: "1px solid #ddd",
                                borderBottom: "none",
                                display: isColumnVisible("budgetTotalProduction") ? "table-cell" : "none",
                                padding: "8px 8px",
                                minWidth: "120px",
                                maxWidth: "150px",
                                width: "130px",
                              }}
                            >
                              {formatCurrency(row.budgetTotalProduction)}
                            </TableCell>
                          )}
                          {/* Production Total Actual Cost (only for recording/unlocked) */}
                          {(showProductionTotals) && (
                            <TableCell
                              align="right"
                              sx={{ 
                                borderRight: "1px solid #ddd",
                                borderBottom: "none",
                                display: isColumnVisible("totalItemActualCostProduction") ? "table-cell" : "none",
                                padding: "8px 8px",
                                minWidth: "120px",
                                maxWidth: "150px",
                                width: "130px",
                              }}
                            >
                              {formatCurrency(row.totalItemActualCostProduction)}
                            </TableCell>
                          )}
                          {/* Empty column cell */}
                          <TableCell
                            align="center"
                            sx={{ 
                              borderRight: "1px solid #ddd",
                              borderBottom: "none",
                              padding: "8px 16px",
                              minWidth: "50px",
                              width: "50px",
                            }}
                          >
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        </Grid>
        {selectedItem && (selectedColumn > 0 || selectedRecordingEpisodes) && (
          <Drawer
            anchor="right"
            open={Boolean(selectedItem)}
            onClose={handlePanelClose}
            PaperProps={{
              style: { width: "520px" },
            }}
          >
            <ProductionStatusDetails
              item={selectedItem}
              column={selectedColumn}
              episodes={selectedRecordingEpisodes}
              recordingLabel={selectedRecordingLabel}
              recordingEpisodeRange={selectedRecordingEpisodeRange}
              details={itemsDetails}
              itemName={selectedItemName}
              budget={selectedBudget}
              threshold={threshold}
              onClose={handlePanelClose}
              onEditExpense={setEditExpenseId}
              onEditExpenseDetails={setEditExpenseDetailsId}
            />
          </Drawer>
        )}

        {editExpenseId && (
          <ProductionEdit id={editExpenseId} onClose={handleExpenseEditClose} />
        )}
        {editExpenseDetailsId && (
          <ExpenseDetailsEdit
            id={editExpenseDetailsId}
            onClose={handleExpenseDetailsEditClose}
          />
        )}
      </Grid>
    </Box>
  );
};

export default EpisodeGrid;
